/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: a set of workouts, done in any order, any time.
 */
import React, { useEffect } from "react";
import { useProgramWorkouts } from "../../../hooks/useProgramWorkouts";
import { WorkoutSlot } from "./WorkoutSlot";
import { useWorkoutNotes } from "../../../hooks/useWorkoutNotes";
import { usePlannerEditing } from "./usePlannerEditing";
import type { PlannerProps } from "./plannerProps";

export function ListPlanner({
  program,
  canManage,
  request,
  onRequestHandled,
  noteLabel,
}: PlannerProps) {
  const data = useProgramWorkouts(program.programmingUID, { kind: "all" });
  const notes = useWorkoutNotes(
    program.programmingUID,
    data.workouts.map((w) => w.id),
    canManage && Boolean(noteLabel)
  );
  const notesFor = noteLabel
    ? {
        label: noteLabel,
        byId: notes.notes,
        failed: notes.status === "error",
        onRetry: notes.reload,
      }
    : undefined;
  const editing = usePlannerEditing({
    programName: program.name,
    workouts: data,
    notes:
      canManage && noteLabel
        ? { label: noteLabel, status: notes.status, byId: notes.notes, save: notes.save }
        : undefined,
    describe: () => "In any order",
  });

  useEffect(() => {
    if (!request) return;
    if (request === "add") editing.openCreate({ date: null });
    onRequestHandled();
  }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  const count = data.workouts.length;
  return (
    <div className="flex flex-col gap-4" data-testid="list-planner">
      <WorkoutSlot
        heading={
          data.status === "ready" ? `${count} ${count === 1 ? "workout" : "workouts"}` : "Workouts"
        }
        workouts={data.workouts}
        status={data.status}
        canManage={canManage}
        emptyText={
          canManage
            ? "No workouts yet. Add the first one; members can do them in any order."
            : "No workouts yet."
        }
        onAdd={() => editing.openCreate({ date: null })}
        onEdit={editing.openEdit}
        onDuplicate={editing.duplicate}
        onRemove={editing.requestRemove}
        onRetry={data.reload}
        notes={canManage ? notesFor : undefined}
      />
      {editing.nodes}
    </div>
  );
}
